/**
 * `03/ CRF/ List` (12:582) — THE WORK REGISTER at `/countries/:cc/crfs`.
 *
 * ===========================================================================
 * IT IS NOT ONLY THE CRF REGISTER ANY MORE, AND THAT HAPPENED WITHOUT IT
 * ===========================================================================
 * `CrfsService.list` has no predicate that could exclude a project (design
 * §5.2a), so from the day migration 37 shipped this read CONTAINED projects
 * whether or not anybody decided it should. The decision is therefore made
 * explicitly here and on the server: the default is `work_type=crf`, because
 * this screen was the CRF register and its users' universe must not widen under
 * them without an act, and All / CRFs / Projects is a control somebody chooses.
 *
 * ⚠ THAT SEGMENT IS SERVER-SIDE, and the paragraph below is the reason it had to
 * be. The register is paged; a segment applied to the fetched page would show the
 * projects on page 1 and call that the project register.
 *
 * ===========================================================================
 * WHAT THE STAGE SEGMENTS ARE, AND WHAT THEY DELIBERATELY ARE NOT
 * ===========================================================================
 * The design's "Pending 6 / Completed 18" segments partition the register over
 * stage SETS, but the server's only list filter is per-stage (`?status=`). A
 * segment that filtered client-side across one fetched page would show "Pending"
 * rows from page 1 only and call that the pending register — a quiet lie. So the
 * segment counts render as INFORMATION, and the controls beneath them are
 * per-stage chips, one per stage that holds rows, each setting the one filter
 * the server actually answers.
 *
 * ===========================================================================
 * THE PLATFORM AND STACK COUNTS SUM TO MORE THAN THE ROW COUNT, ON PURPOSE
 * ===========================================================================
 * A job that ships to iOS and Android is counted under both, because the owner's
 * question is "how much iOS work do we have", not "partition the register". So
 * the buckets can total more than the register does — and, when records carry no
 * platform at all, LESS. Both directions are legal, which is why the screen SAYS
 * so beside the numbers rather than leaving somebody to file the arithmetic as a
 * defect later. The counts are SERVER aggregates over the whole filtered set; a
 * tally of the fetched page would be the same quiet lie as a client-side segment.
 *
 * ===========================================================================
 * THE QUOTED-VALUE CARD DOES NO ARITHMETIC ON MONEY
 * ===========================================================================
 * `amount` arrives as the figure OR as '•••' (the masking interceptor's call,
 * never this client's). A sum over a page holding one mask is `NaN`, and a sum
 * that silently skipped the mask would misstate the total — so when ANY amount
 * on the page is masked the card says so in words, and otherwise it shows the
 * one figure that is honest without a server total: how many CRFs on this page
 * carry a price. No server aggregate exists to quote, and inventing one
 * client-side is exactly what the quotes feature's money module forbids.
 *
 * KPI counts follow the StageCountCards discipline: a failed query renders
 * ABSENT, never 0 — "there is nothing pending" is the wrong thing to tell
 * somebody whose count request 500'd.
 *
 * The only bulk verb is Export CSV over the SELECTED rows' visible values —
 * masked amounts export as '•••' because the mask holds on every channel
 * (rule 14). No other bulk action exists on this API, so none is drawn.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Minus, Plus, Search, X } from 'lucide-react';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Checkbox, EmptyState, SkeletonBlock, SkeletonRows } from '../../components/ui';
import { relativeTime } from '../../domain/events';
import { formatDateLong, formatMoney, isMaskedAmount } from '../inquiries/format';
import { cn } from '../../lib/cn';
import {
  crfKeys,
  fetchAwaitingConfirmation,
  fetchCrfs,
  fetchCrfStatusCounts,
  fetchCrfVocabularyCounts,
} from './api';
import type { CrfCountFilters, CrfDto, CrfListFilters } from './api';
import {
  CRF_COMPLETED_STAGES,
  CRF_PENDING_STAGES,
  CRF_STAGES,
  WORK_TYPES,
  WORK_TYPE_LABEL,
  platformsSummary,
} from './domain';
import { WorkTypeBadge } from './WorkTypeBadge';
import { crfErrorText } from './errors';

const PER_PAGE = 50;

/** Register order: the pending set, then the completed set — the enum's own order. */
const ALL_STAGES: readonly string[] = [...CRF_PENDING_STAGES, ...CRF_COMPLETED_STAGES];

/**
 * THE SEGMENT'S THREE ANSWERS — `?work_type=crf|project|all`, the server's vocabulary.
 *
 * ⚠ `all` IS NOT A WORK TYPE. It is the ABSENCE of a narrowing, and it is spelled the way
 * the server spells it so the URL and the wire say the same word. The two real members come
 * from `WORK_TYPES` rather than being retyped here, so a third work type reddens the mirror
 * test and lands in this control without anybody remembering to add it.
 *
 * ⚠ AND THE LABELS ARE PLURAL NOUNS BECAUSE THIS IS A SET, NOT A ROW. `WORK_TYPE_LABEL` is
 * the singular the BADGE uses — one record is "Project"; this control chooses "Projects".
 */
const SEGMENT_ALL = 'all';
const WORK_TYPE_SEGMENTS: readonly { value: string; label: string }[] = [
  { value: SEGMENT_ALL, label: 'All work' },
  ...WORK_TYPES.map((value) => ({ value, label: `${WORK_TYPE_LABEL[value]}s` })),
];

export function CrfsListPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'crf.view');
  const canCreate = can(countryCode, 'crf.create');

  const rawStatus = params.get('status');
  const status = rawStatus !== null && Object.hasOwn(CRF_STAGES, rawStatus) ? rawStatus : null;
  const q = params.get('q') ?? '';
  /*
   * ⚠ ABSENT MEANS THE SERVER'S DEFAULT, AND THIS CLIENT DOES NOT NAME IT. Sending
   * `work_type=crf` on a bare URL would put the default in two places — here and in
   * `assertWorkTypeFilter` — and the day one of them moves the KPI strip and the list would
   * disagree about which universe they describe. The empty string means "I did not ask",
   * and the SEGMENT below reads as CRFs because that is what the server answers.
   *
   * The value is passed through UNVALIDATED, exactly as `?range=` is on the renewals
   * register: the server owns the vocabulary and refuses a stranger with a 400 that names
   * it. Sanitising here would render the whole register while the segment claimed a filter.
   */
  const workType = params.get('work_type') ?? '';
  const platform = params.get('platform') ?? '';
  const stack = params.get('stack') ?? '';
  /*
   * AM-1's one visible toggle (v24 A1.4). OFF is the ABSENT parameter — the
   * server owns the default (terminal legacy rows hidden on a browse; a `q`
   * search reaches everything), and naming it here would put it in two places,
   * the `work_type` lesson above. ON goes on the URL as `include_history=1`,
   * so the toggle is plainly visible state a colleague's pasted link carries.
   */
  const includeHistory = params.get('include_history') === '1';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  /** The narrowing BOTH count strips and the list must share — see `crfKeys.counts`. */
  const countFilters = useMemo<CrfCountFilters>(
    () => ({
      work_type: workType === '' ? undefined : workType,
      platform: platform === '' ? undefined : platform,
      stack: stack === '' ? undefined : stack,
      include_history: includeHistory ? '1' : undefined,
    }),
    [workType, platform, stack, includeHistory],
  );

  const filters = useMemo<CrfListFilters>(
    () => ({
      status: status ?? undefined,
      q: q === '' ? undefined : q,
      ...countFilters,
      page,
      perPage: PER_PAGE,
    }),
    [status, q, countFilters, page],
  );

  const list = useQuery({
    queryKey: crfKeys.list(countryCode, filters),
    queryFn: ({ signal }) => fetchCrfs(countryCode, filters, signal),
    enabled: canView,
  });
  const counts = useQuery({
    queryKey: crfKeys.counts(countryCode, countFilters),
    queryFn: ({ signal }) => fetchCrfStatusCounts(countryCode, countFilters, signal),
    enabled: canView,
  });
  /*
   * ⚠ A SEPARATE QUERY, NOT A SECOND FIELD ON `counts`. If a vocabulary count 500s, the
   * status strip above it must still render its own numbers — one payload would have made
   * a failure in either take both down, and this screen's rule is that a failed count is
   * ABSENT rather than 0, per count, not per page.
   */
  const vocabulary = useQuery({
    queryKey: crfKeys.vocabularyCounts(countryCode, countFilters),
    queryFn: ({ signal }) => fetchCrfVocabularyCounts(countryCode, countFilters, signal),
    enabled: canView,
  });
  // The A5 register's own total, at perPage 1 — one row of payload for one number.
  const awaiting = useQuery({
    queryKey: crfKeys.awaiting(countryCode, 1),
    queryFn: ({ signal }) => fetchAwaitingConfirmation(countryCode, { page: 1, perPage: 1 }, signal),
    enabled: canView,
  });

  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());

  /**
   * Selection is CLEARED when the result set's identity changes — the
   * InquiriesListPage contract, same mechanism: React's "adjust state when a
   * prop changes" pattern rather than an effect, so no render can commit a
   * stale selection against fresh rows. A selection that survived a filter
   * change would let Export CSV ship rows the operator can no longer see.
   */
  const listIdentity = useMemo(() => JSON.stringify(filters), [filters]);
  const [selectionScope, setSelectionScope] = useState(listIdentity);
  if (selectionScope !== listIdentity) {
    setSelectionScope(listIdentity);
    if (selected.size > 0) setSelected(new Set());
  }

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="CRFs are not available to this account">
          Viewing the CRF register in {countryCode} needs the CRF permission. Ask an
          administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const filtered = q.trim() !== '' || status !== null;

  const stageCounts = counts.data;
  const pendingTotal =
    stageCounts === undefined
      ? null
      : CRF_PENDING_STAGES.reduce((sum, stage) => sum + (stageCounts[stage] ?? 0), 0);
  const completedTotal =
    stageCounts === undefined
      ? null
      : CRF_COMPLETED_STAGES.reduce((sum, stage) => sum + (stageCounts[stage] ?? 0), 0);

  // Both money columns share one caller-side mask decision, so either being
  // masked means this caller cannot see amounts at all.
  const anyMasked = rows.some(
    (row) => isMaskedAmount(row.amount) || isMaskedAmount(row.advance_amount),
  );
  const pricedCount = rows.filter(
    (row) => row.amount !== null && row.amount.trim() !== '' && !isMaskedAmount(row.amount),
  ).length;

  const setStatus = (next: string | null): void => {
    const patch = new URLSearchParams(params);
    if (next === null) patch.delete('status');
    else patch.set('status', next);
    // A filter change resets paging — page 3 of one stage is not page 3 of another.
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  /**
   * The segment. `all` and the two members go on the URL; nothing means the server's
   * default, so "CRFs" is chosen by DELETING the parameter rather than by naming it — see
   * the `workType` reader above on why this client must not spell the default.
   *
   * ⚠ IT CLEARS PAGING AND IT CLEARS `?stack=`. A stack is a project-only vocabulary
   * (D-2b), so `work_type=crf&stack=Flutter` is a filter pair that can only ever return
   * nothing — and an empty register reads as "there is no such work", which is the wrong
   * answer to a question nobody meant to ask.
   */
  const setWorkType = (next: string): void => {
    const patch = new URLSearchParams(params);
    if (next === 'crf') patch.delete('work_type');
    else patch.set('work_type', next);
    if (next === 'crf') patch.delete('stack');
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  /**
   * One vocabulary chip toggles ITS OWN id in a comma-separated axis.
   *
   * The server reads each axis as an OR and the two axes as an AND, so ticking iOS and
   * Android asks for work touching either, and adding Flutter narrows that to the ones
   * built in Flutter. That is the shape of the owner's questions, and it is the server's
   * rule — this function only edits the parameter.
   */
  const toggleVocabulary = (key: 'platform' | 'stack', id: string): void => {
    const current = (params.get(key) ?? '').split(',').filter((part) => part !== '');
    const next = current.includes(id)
      ? current.filter((part) => part !== id)
      : [...current, id];
    const patch = new URLSearchParams(params);
    if (next.length === 0) patch.delete(key);
    else patch.set(key, next.join(','));
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  const setQ = (next: string): void => {
    const patch = new URLSearchParams(params);
    if (next === '') patch.delete('q');
    else patch.set('q', next);
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  /** AM-1's toggle — OFF deletes the parameter (the server's default is not named). */
  const setIncludeHistory = (next: boolean): void => {
    const patch = new URLSearchParams(params);
    if (next) patch.set('include_history', '1');
    else patch.delete('include_history');
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  /**
   * The one bulk verb. The CSV carries the VISIBLE column values — the same
   * strings the cells render — so a masked amount exports as '•••', never as a
   * digit this client was refused (rule 14: the mask holds on every channel).
   */
  const exportSelection = (): void => {
    const chosen = rows.filter((row) => selected.has(row.id));
    if (chosen.length === 0) return;
    const blob = new Blob([crfsCsv(chosen)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${countryCode.toLowerCase()}-crfs.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      {/*
        ⚠ THE TITLE FOLLOWS THE SEGMENT, because the screen genuinely shows a different
        universe in each. Calling it "CRFs" while it lists projects is the defect design
        D-8 refuses to fix with a disambiguating line, and calling it "Work" while it is
        narrowed to CRFs would be the same untruth pointed the other way.
      */}
      <PageHeader
        title={
          workType === SEGMENT_ALL
            ? 'Work'
            : workType === 'project'
              ? 'Projects'
              : 'CRFs'
        }
        subtitle={
          list.data === undefined
            ? undefined
            : `${String(list.data.total)} ${list.data.total === 1 ? 'record' : 'records'}`
        }
        actions={
          // D5 — a gated action the caller cannot perform is ABSENT, not disabled.
          // A real anchor, not a Button with an onClick: middle-click and
          // copy-link-address must work (the QuotesListPage precedent).
          canCreate ? (
            <Link
              to="new"
              className="inline-flex h-9 items-center gap-2 rounded-sm bg-indigo px-4 text-body font-medium text-fg-inverse hover:bg-indigo/90"
            >
              <Plus aria-hidden="true" className="size-4" />
              Create CRF
            </Link>
          ) : undefined
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard
          label="Awaiting confirmation"
          loading={awaiting.isPending}
          failed={awaiting.isError}
          value={awaiting.data === undefined ? null : awaiting.data.total}
        />
        <KpiCard
          label="Pending"
          loading={counts.isPending}
          failed={counts.isError}
          value={pendingTotal}
        />
        {/* NO CLIENT-SIDE SUM — see the file header. */}
        <KpiCard
          label="Quoted value"
          loading={list.isPending}
          failed={list.isError}
          value={anyMasked ? '—' : pricedCount}
          detail={
            anyMasked
              ? 'Totals need amounts access.'
              : 'The number of CRFs on this page that carry a quoted amount.'
          }
        />
      </div>

      {/*
        THE WORK-TYPE SEGMENT — server-side (see the file header). It is rendered whether or
        not any count answered, because it is a CONTROL: a filter that disappears when a
        count fails would strand an operator on a universe they cannot leave.
      */}
      <div
        role="group"
        aria-label="Filter by work type"
        className="flex flex-wrap items-center gap-2"
      >
        {WORK_TYPE_SEGMENTS.map((segment) => (
          <StageChip
            key={segment.value}
            label={segment.label}
            count={null}
            active={
              segment.value === 'crf' ? workType === '' || workType === 'crf' : workType === segment.value
            }
            onSelect={() => setWorkType(segment.value)}
          />
        ))}
      </div>

      <VocabularyCounts
        loading={vocabulary.isPending}
        failed={vocabulary.isError}
        platforms={vocabulary.data?.platforms}
        stacks={vocabulary.data?.stacks}
        selectedPlatforms={platform}
        selectedStacks={stack}
        onToggle={toggleVocabulary}
      />

      {stageCounts === undefined || pendingTotal === null || completedTotal === null ? null : (
        <div className="flex flex-col gap-2">
          {/* Information, not a control — the reasoning is in the file header. */}
          <p className="tnum text-label text-fg-secondary">
            Pending {pendingTotal} / Completed {completedTotal}
          </p>
          <div role="group" aria-label="Filter by stage" className="flex flex-wrap items-center gap-2">
            <StageChip
              label="All"
              count={null}
              active={status === null}
              onSelect={() => setStatus(null)}
            />
            {ALL_STAGES.filter(
              (stage) => (stageCounts[stage] ?? 0) > 0 || stage === status,
            ).map((stage) => (
              <StageChip
                key={stage}
                label={CRF_STAGES[stage]?.label ?? stage}
                count={stageCounts[stage] ?? 0}
                active={status === stage}
                onSelect={() => setStatus(stage)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <CrfSearchField value={q} onChange={setQ} />
        {/*
          AM-1 (v24 A1.4): deployed/cancelled/closed LEGACY records sit behind this;
          open legacy work is in the register regardless. It is a checkbox in the
          toolbar — "plainly visible state, never a hidden preference" — and the
          search field beside it reaches everything with or without it (the server's
          rule, proven in legacy-filter.e2e-spec.ts r3).
        */}
        <Checkbox
          label="Include history"
          checked={includeHistory}
          onChange={(event) => setIncludeHistory(event.target.checked)}
        />
      </div>

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading CRFs" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The CRF register could not be loaded">
          <p>{crfErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        // An empty PAGE is not an empty RESULT SET — the quotes list's V3 lesson.
        <Alert tone="info" title={`Page ${String(list.data.page)} is past the end of this list`}>
          <p>The register holds {String(total)} {total === 1 ? 'record' : 'records'}.</p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        filtered ? (
          <EmptyState
            title="No CRFs match this view"
            description="Nothing in the register matches the current search or stage filter."
            action={
              <button
                type="button"
                onClick={() => {
                  const patch = new URLSearchParams();
                  setParams(patch, { replace: true });
                }}
                className="text-body13 font-medium text-indigo hover:underline"
              >
                Clear the filters.
              </button>
            }
          />
        ) : (
          <EmptyState
            title="No CRFs yet"
            description="The register is empty until the first customer requirement is recorded."
            action={
              canCreate ? (
                <Link
                  to="new"
                  className="inline-flex h-9 items-center gap-2 rounded-sm bg-indigo px-4 text-body font-medium text-fg-inverse hover:bg-indigo/90"
                >
                  Create the first CRF
                </Link>
              ) : undefined
            }
          />
        )
      ) : (
        <CrfTable
          rows={rows}
          selected={selected}
          onSelect={(id, next) =>
            setSelected((current) => {
              const copy = new Set(current);
              if (next) copy.add(id);
              else copy.delete(id);
              return copy;
            })
          }
          onSelectPage={(next) =>
            setSelected(next ? new Set(rows.map((row) => row.id)) : new Set())
          }
        />
      )}

      {/* Every paging number below comes off the one payload the rows came off. */}
      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}

      {selected.size === 0 ? null : (
        <div
          role="region"
          aria-label="Bulk actions"
          // z-40 keeps it under the toast stack at z-50 — the BulkBar precedent.
          className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-pill border border-line bg-surface py-2 pl-5 pr-3 shadow-[0_12px_16px_0_rgba(28,28,28,0.18)]"
        >
          <p className="tnum whitespace-nowrap text-body font-medium text-fg">
            {selected.size} selected
          </p>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-body13 text-indigo hover:underline"
          >
            Clear
          </button>
          <span aria-hidden="true" className="h-5 w-px shrink-0 bg-line-strong" />
          <button
            type="button"
            onClick={exportSelection}
            className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-sm px-2.5 text-body font-medium text-fg hover:bg-subtle"
          >
            Export CSV
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── KPI cards ── */

/**
 * The StageCountCards shell — `bg/surface` + `border/default` + `radius/md`,
 * label line then a `KPI/28` number — with its discipline: a FAILED count is
 * ABSENT, stated in a sentence, never rendered as 0.
 */
function KpiCard({
  label,
  loading,
  failed,
  value,
  detail,
}: {
  label: string;
  loading: boolean;
  failed: boolean;
  value: React.ReactNode;
  detail?: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-line bg-surface p-5">
      <span className="text-body font-medium text-fg">{label}</span>
      {loading ? (
        <SkeletonBlock className="h-8 w-14" />
      ) : failed ? (
        <span className="text-label text-fg-secondary">This count did not answer.</span>
      ) : (
        <>
          <span className="tnum text-kpi text-fg">{value}</span>
          {detail === undefined ? null : (
            <span className="text-label text-fg-secondary">{detail}</span>
          )}
        </>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────── vocabulary counts ── */

/**
 * ⚠ THE SENTENCE BELOW IS THE POINT OF THIS COMPONENT, NOT DECORATION.
 *
 * A per-platform total legitimately EXCEEDS the row count — a job that ships to iOS and
 * Android is counted under both — and legitimately falls SHORT of it, because a record may
 * carry no platform at all. Neither is a bug, both are properties of the data, and neither
 * can be engineered away without answering a different question from the one the owner
 * asked ("how much iOS work do we have", not "partition the register").
 *
 * So it is SAID, on the screen, beside the numbers. The alternative is somebody adding the
 * column up, finding it does not match the header, and filing a defect that is correct
 * about the arithmetic and wrong about the design — which costs more than one line of copy.
 *
 * ⚠ THE WORDING IS PINNED BY A TEST for that reason. If it is reworded, reword the test.
 */
const VOCABULARY_NOTE = 'counts by platform; a job appears under each platform it ships to';

/**
 * The two vocabulary axes, as counts that are also filters.
 *
 * ⚠ THE KPI DISCIPLINE HOLDS AND IT IS PER STRIP: a failed count renders ABSENT with the
 * failure stated, NEVER 0. "There is no iOS work" is the wrong thing to tell somebody whose
 * count request 500'd, and it is a particularly expensive wrong thing here, because the
 * question these numbers answer is a staffing one.
 *
 * ⚠ A VALUE WITH NO WORK IS STILL LISTED, AT 0. The server zero-fills, and dropping the
 * zeros here would make "no Flutter work this quarter" and "Flutter is not something we
 * track" look identical — the same conflation the status strip refuses.
 */
function VocabularyCounts({
  loading,
  failed,
  platforms,
  stacks,
  selectedPlatforms,
  selectedStacks,
  onToggle,
}: {
  loading: boolean;
  failed: boolean;
  platforms: readonly { id: string; name: string; count: number }[] | undefined;
  stacks: readonly { id: string; name: string; count: number }[] | undefined;
  selectedPlatforms: string;
  selectedStacks: string;
  onToggle: (key: 'platform' | 'stack', id: string) => void;
}): React.JSX.Element | null {
  if (loading) return <SkeletonBlock className="h-8 w-64" />;
  if (failed) {
    return (
      <p className="text-label text-fg-secondary">
        The platform and stack counts did not answer, so none are shown. The register below
        is unaffected.
      </p>
    );
  }
  if (platforms === undefined || stacks === undefined) return null;
  if (platforms.length === 0 && stacks.length === 0) return null;

  const chosenPlatforms = new Set(selectedPlatforms.split(',').filter((part) => part !== ''));
  const chosenStacks = new Set(selectedStacks.split(',').filter((part) => part !== ''));

  return (
    <div className="flex flex-col gap-2">
      <p className="text-label text-fg-secondary" data-testid="vocabulary-note">
        {VOCABULARY_NOTE}
      </p>
      {platforms.length === 0 ? null : (
        <div
          role="group"
          aria-label="Filter by platform"
          className="flex flex-wrap items-center gap-2"
        >
          {platforms.map((entry) => (
            <StageChip
              key={entry.id}
              label={entry.name}
              count={entry.count}
              active={chosenPlatforms.has(entry.id)}
              onSelect={() => onToggle('platform', entry.id)}
            />
          ))}
        </div>
      )}
      {stacks.length === 0 ? null : (
        <div
          role="group"
          aria-label="Filter by stack"
          className="flex flex-wrap items-center gap-2"
        >
          {stacks.map((entry) => (
            <StageChip
              key={entry.id}
              label={entry.name}
              count={entry.count}
              active={chosenStacks.has(entry.id)}
              onSelect={() => onToggle('stack', entry.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── stage chips ── */

/** The Toolbar filter-chip geometry (6:467), carrying a stage and its count. */
function StageChip({
  label,
  count,
  active,
  onSelect,
}: {
  label: string;
  count: number | null;
  active: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5 text-body13',
        active ? 'border-line-strong font-medium text-fg' : 'text-fg-secondary hover:text-fg',
      )}
    >
      {label}
      {count === null ? null : (
        <span className={cn('tnum text-label', active ? 'text-indigo' : 'text-fg-muted')}>
          {count}
        </span>
      )}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────── search ── */

/**
 * `?q=` — the Toolbar SearchField pattern (mirror + debounce), at 300ms. The URL
 * updates with `replace` so a six-character search leaves one history entry.
 */
function CrfSearchField({
  value,
  onChange,
}: {
  value: string;
  onChange: (q: string) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState(value);
  const [mirrored, setMirrored] = useState(value);

  if (value !== mirrored) {
    setMirrored(value);
    setDraft(value);
  }

  useEffect(() => {
    if (draft === value) return;
    const timer = window.setTimeout(() => onChange(draft), 300);
    return () => window.clearTimeout(timer);
  }, [draft, value, onChange]);

  return (
    <div className="flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5">
      <Search aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" />
      <input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Search number or customer"
        aria-label="Search CRFs by number or customer"
        className="w-[200px] min-w-0 bg-transparent text-body13 text-fg outline-none placeholder:text-fg-muted"
      />
      {draft === '' ? null : (
        <button
          type="button"
          onClick={() => {
            setDraft('');
            onChange('');
          }}
          aria-label="Clear search"
          className="shrink-0 text-fg-muted hover:text-fg"
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── table ── */

const HEADERS = [
  'CRF ID',
  'CUSTOMER',
  // Migration 38: the column shows the SHARED vocabulary now, so it stops naming
  // the table that is gone. 'Customization' is the Create screen's own section label.
  'CUSTOMIZATION',
  'QUOTED',
  'ADVANCE',
  // Migration 64 (v24 Phase B): amount − received over the CRF's ledger, derived
  // server-side; '—' is PB-4's unresolved state (no established amount), never 0.00.
  'PENDING',
  'DEVELOPER',
  'STAGE',
] as const;

function CrfTable({
  rows,
  selected,
  onSelect,
  onSelectPage,
}: {
  rows: readonly CrfDto[];
  selected: ReadonlySet<string>;
  onSelect: (id: string, next: boolean) => void;
  onSelectPage: (next: boolean) => void;
}): React.JSX.Element {
  const pageSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));
  const someSelected = rows.some((row) => selected.has(row.id));

  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1080px] border-collapse text-left">
        <caption className="sr-only">The CRF register</caption>
        <colgroup>
          <col className="w-12" />
          <col className="w-[170px]" />
          <col />
          <col className="w-[190px]" />
          <col className="w-[130px]" />
          <col className="w-[130px]" />
          <col className="w-[160px]" />
          <col className="w-[180px]" />
          <col className="w-[160px]" />
        </colgroup>

        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            <th scope="col" className="px-0 text-center align-middle">
              <CellCheckbox
                checked={pageSelected}
                indeterminate={someSelected}
                label={`Select all ${String(rows.length)} CRFs on this page`}
                onChange={onSelectPage}
              />
            </th>
            {HEADERS.map((header) => (
              <th
                key={header}
                scope="col"
                className={cn(
                  'px-3 text-th uppercase text-fg-muted',
                  (header === 'QUOTED' || header === 'ADVANCE' || header === 'PENDING') && 'text-right',
                )}
              >
                {header}
              </th>
            ))}
            {/* Server-ordered, newest first — stated, not a dead sort control. */}
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              UPDATED <span aria-hidden="true">↓</span>
              <span className="sr-only">, newest first</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const isSelected = selected.has(row.id);
            const updatedIso = row.updated_at ?? row.created_at;
            return (
              <tr
                key={row.id}
                className={cn(
                  'crf-register-row border-b border-line last:border-b-0',
                  isSelected ? 'bg-indigo-soft dark:bg-indigo/20' : 'hover:bg-subtle',
                )}
              >
                <td className="text-center align-middle">
                  <CellCheckbox
                    checked={isSelected}
                    label={`Select ${row.doc_no}`}
                    onChange={(next) => onSelect(row.id, next)}
                  />
                </td>

                <td className="px-3 align-middle">
                  {/*
                    The row's one action IS this link — no "…" menu with nothing in it.

                    The BADGE beside it marks a project. The document number already
                    distinguishes the two for a reader who parses it (`IN-PROJ/…` against
                    `IN-CRF/…`), which is exactly why the badge is not redundant: a
                    distinction that depends on a human reading a string is not one a
                    screenshot, a colour-blind reader or a CSV can act on, and the CSV
                    below carries the same word for the same reason.
                  */}
                  <span className="flex items-center gap-2">
                    <Link
                      to={row.id}
                      className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
                    >
                      {row.doc_no}
                    </Link>
                    <WorkTypeBadge workType={row.work_type} />
                  </span>
                </td>

                <td className="px-3 align-middle text-body13 text-fg">
                  {row.party_name ?? 'Unknown customer'}
                </td>

                <td className="px-3 align-middle text-body13 text-fg-secondary">
                  {platformsSummary((row.platforms ?? []).map((entry) => entry.name))}
                </td>

                <MoneyCell value={row.amount} currency={row.currency} />
                <MoneyCell value={row.advance_amount} currency={row.currency} />
                <MoneyCell value={row.pending_amount} currency={row.currency} />

                <td className="px-3 align-middle">
                  {row.developer === null || row.developer === undefined ? (
                    <span className="inline-flex items-center rounded-pill bg-subtle px-2.5 py-1 text-label text-fg-muted ring-1 ring-line">
                      Unassigned
                    </span>
                  ) : (
                    <span className="truncate text-body13 text-fg">{row.developer.name}</span>
                  )}
                </td>

                <td className="px-3 align-middle">
                  <CrfStagePill status={row.status} />
                </td>

                <td className="px-3 align-middle">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-body13 text-fg">{formatDateLong(updatedIso)}</span>
                    <span className="text-label text-fg-muted">{relativeTime(updatedIso)}</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The masked-cell idiom, verbatim from `InquiryTable`: masked renders QUIETER than a real
 * figure, with the reason in a `title`, and the value is NEVER parsed — `Number('•••')`
 * is NaN, and a register column quietly rendering NaN would tell an operator
 * the work is worthless.
 *
 * ⚠ THE QUIET TOKEN IS `text/secondary`, AND IT USED TO BE `text/muted` — a MEASURED AA
 * failure at **2.63:1** on `bg/surface` in light mode (2.32:1 on a SELECTED row, which is
 * `bg-indigo-soft`, the worst text pair measured anywhere in this app). `text/muted` is
 * classed as DECORATION by the token suite and pinned under 3:1; `•••` is not decoration,
 * it is the cell's entire content. `text/secondary` measures 4.64 light / 5.85 dark here.
 * Fixed as part of the app-wide masked-marker sweep, not invented locally.
 */
function MoneyCell({
  value,
  currency,
}: {
  value: string | null;
  currency: string | null;
}): React.JSX.Element {
  const masked = isMaskedAmount(value);
  return (
    <td className="whitespace-nowrap px-3 text-right align-middle">
      <span
        className={cn('tnum text-body', masked ? 'text-fg-secondary' : 'text-fg')}
        title={masked ? 'Hidden — this account cannot view amounts in this country' : undefined}
      >
        {formatMoney(value, currency)}
      </span>
    </td>
  );
}

/* ─────────────────────────────────────────────────────────────── stage pill ── */

type CrfStageTone = (typeof CRF_STAGES)[string]['tone'];

/**
 * The `Pill/Stage` object rendered from the CRF tone map. A local pill, not a
 * widened `StagePill`, for the reason `features/quotes/status.tsx` records: that
 * component's prop is `InquiryStage`, and a union of unrelated enums would let
 * nonsense compile. Every class pair below already exists in `domain/quotes.ts`
 * or `domain/stages.ts` — no colour is invented here.
 */
const STAGE_PILL_CLASSES: Readonly<Record<CrfStageTone, string>> = {
  violet: 'bg-stage-confirm-bg text-stage-confirm-fg',
  amber: 'bg-warn-bg text-warn-fg',
  blue: 'bg-info-bg text-info-fg',
  green: 'bg-ok-bg text-ok-fg',
  slate: 'bg-subtle text-fg ring-1 ring-line',
  red: 'bg-stage-lost-bg text-stage-lost-fg',
};

function CrfStagePill({ status }: { status: string }): React.JSX.Element {
  const descriptor = CRF_STAGES[status];
  // A stage this client does not know renders as an honest neutral chip with
  // the server's own word — never a blank pill, never a guessed colour.
  const tone: CrfStageTone = descriptor?.tone ?? 'slate';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
        STAGE_PILL_CLASSES[tone],
      )}
    >
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
      {descriptor?.label ?? status}
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────── checkbox ── */

/**
 * `Table/CheckboxCell` — the InquiryTable checkbox, re-authored here because
 * that component is file-private. Same geometry, same peer-driven tick/dash,
 * same reason it is not the shared `Checkbox`: a table cell needs an invisible
 * label, and `indeterminate` is a property only a ref can set.
 */
function CellCheckbox({
  checked,
  indeterminate = false,
  label,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}): React.JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <span className="relative inline-flex size-4 align-middle">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        aria-label={label}
        onChange={(event) => onChange(event.target.checked)}
        className="peer absolute inset-0 size-4 cursor-pointer appearance-none rounded-[4px] border-[1.5px] border-line-strong bg-surface checked:border-indigo checked:bg-indigo indeterminate:border-indigo indeterminate:bg-indigo"
      />
      <Check
        aria-hidden="true"
        strokeWidth={3}
        className="pointer-events-none absolute inset-0 m-auto size-3 text-fg-inverse opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-0"
      />
      <Minus
        aria-hidden="true"
        strokeWidth={3}
        className="pointer-events-none absolute inset-0 m-auto size-3 text-fg-inverse opacity-0 peer-indeterminate:opacity-100"
      />
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────── pager ── */

/** `Table/Pagination` — the per-page readout, the range, and the two chevrons. */
function PagerFooter({
  page,
  perPage,
  shown,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  shown: number;
  total: number;
  onPage: (page: number) => void;
}): React.JSX.Element {
  const firstOnPage = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastOnPage = (page - 1) * perPage + shown;
  const hasPrev = page > 1;
  const hasNext = lastOnPage < total;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-2">
      <p className="text-body13 text-fg-secondary">{perPage} per page</p>
      <div className="flex items-center gap-2">
        <p className="tnum text-body13 text-fg-secondary">
          {firstOnPage}–{lastOnPage} of {total}
        </p>
        <PageButton label="Previous page" disabled={!hasPrev} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden="true" className="size-4" />
        </PageButton>
        <PageButton label="Next page" disabled={!hasNext} onClick={() => onPage(page + 1)}>
          <ChevronRight aria-hidden="true" className="size-4" />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/* ──────────────────────────────────────────────────────────────────── CSV ── */

/**
 * ⚠ `Work type` IS A COLUMN OF ITS OWN HERE, and it is the third channel the badge exists
 * for. A spreadsheet has no tints and no pills; if the distinction lived only in the pixel
 * treatment, every export would silently merge CRFs and projects into one undifferentiated
 * list — and an export is precisely where somebody sorts, groups and counts.
 */
const CSV_HEADERS = [
  'CRF ID',
  'Work type',
  'Customer',
  'Customization',
  'Quoted',
  'Advance',
  'Pending',
  'Developer',
  'Stage',
  'Updated',
] as const;

function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** The selected rows' VISIBLE values — the strings the cells render, no more. */
function crfsCsv(rows: readonly CrfDto[]): string {
  const lines = [CSV_HEADERS.join(',')];
  for (const row of rows) {
    lines.push(
      [
        row.doc_no,
        WORK_TYPE_LABEL[row.work_type],
        row.party_name ?? 'Unknown customer',
        platformsSummary((row.platforms ?? []).map((entry) => entry.name)),
        formatMoney(row.amount, row.currency),
        formatMoney(row.advance_amount, row.currency),
        formatMoney(row.pending_amount, row.currency),
        row.developer?.name ?? 'Unassigned',
        CRF_STAGES[row.status]?.label ?? row.status,
        formatDateLong(row.updated_at ?? row.created_at),
      ]
        .map(csvField)
        .join(','),
    );
  }
  return lines.join('\r\n');
}
